import type { TabState } from '../../types';
import { getFiltered } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { MetricCard } from '../ui/MetricCard';
import { SectionHeader } from '../ui/SectionHeader';
import { OfferingChart } from '../charts/OfferingChart';
import { useDeals } from '../../context/DealsContext';
import React from 'react';
interface Props {
  state: TabState;
  searchRows?: React.ReactNode;
}
function regionBadge(region: string) {
  const styles: Record<string, {
    bg: string;
    color: string;
  }> = {
    AMER: {
      bg: '#EFF6FF',
      color: '#1D4ED8'
    },
    EMEA: {
      bg: '#F5F3FF',
      color: '#7C3AED'
    },
    JAPAC: {
      bg: '#F0FDF4',
      color: '#15803D'
    }
  };
  const s = styles[region] || {
    bg: '#F9FAFB',
    color: '#374151'
  };
  return <span className="badge" style={{
    background: s.bg,
    color: s.color,
    fontSize: 8
  }} data-id="element-284">{region}</span>;
}
export function SDPanel({
  state,
  searchRows
}: Props) {
  const {
    deals
  } = useDeals();
  const all = getFiltered('sd', state, deals);
  const ip = all.filter(d => d.status === 'In Progress');
  const aged = ip.filter(d => d.age > 90);
  const scs = new Set(ip.map(d => d.sc)).size;
  const oppGnarr = all.reduce((s, d) => s + (d.opp_gnarr || 0), 0);

  // Closed Booked GNARR: won deals matching region/sub and close_q quarter filter
  const wonDeals = deals.filter(d => {
    if (d.adjusted_commit !== 'Won') return false;
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (state.q !== 'All' || state.fy !== 'All') {
      if (!d.close_q) return false;
      const [yr, qt] = d.close_q.split('-');
      if (state.fy !== 'All' && yr !== state.fy) return false;
      if (state.q !== 'All' && qt !== state.q) return false;
    }
    return true;
  });
  const wonGnarr = wonDeals.reduce((s, d) => s + (d.opp_gnarr || 0), 0);
  const wonCount = wonDeals.length;
  const agedSorted = [...aged].sort((a, b) => b.age - a.age).slice(0, 8);
  const dealsSorted = [...ip].sort((a, b) => b.age - a.age);
  return <div data-id="element-285">
      {/* Metrics */}
      <div className="mrow" data-id="element-286">
        <MetricCard label="In-progress" value={ip.length} sub={`${scs} SCs active`} colorClass="c-blue" data-id="element-287" />
        <MetricCard label="Aged >90 days" value={aged.length} sub="need immediate action" colorClass="c-red" data-id="element-288" />
        <MetricCard label="Closed Booked GNARR" value={fmtG(wonGnarr)} sub={`${wonCount} won opportunit${wonCount === 1 ? 'y' : 'ies'}`} colorClass="c-purple" data-id="element-289" />
        <MetricCard label="GNARR Influenced" value={fmtG(oppGnarr)} sub="total opp GNARR (created qtr)" colorClass="c-green" data-id="element-290" />
      </div>

      {/* Offering chart + Aged deals */}
      <div className="row2" data-id="element-291">
        <div className="card" data-id="element-292">
          <h3 data-id="element-293">Requests by solution</h3>
          <OfferingChart deals={all} data-id="element-294" />
        </div>
        <div className="card" data-id="element-295">
          <h3 data-id="element-296">Aged deals (&gt;90 days) at risk</h3>
          {agedSorted.length > 0 ? <table className="tbl" data-id="element-297">
              <thead data-id="element-298">
                <tr data-id="element-299">
                  <th style={{
                width: '30%'
              }} data-id="element-300">Account</th>
                  <th style={{
                width: '20%'
              }} data-id="element-301">SC</th>
                  <th style={{
                width: '12%'
              }} data-id="element-302">Age</th>
                  <th style={{
                width: '18%'
              }} data-id="element-303">GNARR</th>
                  <th style={{
                width: '12%'
              }} data-id="element-304">Close Q</th>
                  <th style={{
                width: '8%'
              }} data-id="element-305">Sub</th>
                </tr>
              </thead>
              <tbody data-id="element-306">
                {agedSorted.map(d => <tr key={d.id} data-id="element-307">
                    <td title={d.account} data-id="element-308">{d.account.substring(0, 22)}</td>
                    <td title={d.sc} data-id="element-309">{d.sc.split(' ')[0]}</td>
                    <td className="c-red" style={{
                fontWeight: 600
              }} data-id="element-310">{d.age}d</td>
                    <td data-id="element-311">{fmtG(d.gnarr)}</td>
                    <td data-id="element-312">{d.close_q}</td>
                    <td data-id="element-313"><span className="badge b-gray" style={{
                  fontSize: 8
                }} data-id="element-314">{d.sub_region}</span></td>
                  </tr>)}
              </tbody>
            </table> : <div style={{
          padding: '20px',
          textAlign: 'center',
          color: '#8892a0',
          fontSize: 11
        }} data-id="element-315">No aged deals in this filter ✓</div>}
        </div>
      </div>

      {/* Full deals list */}
      <SectionHeader title="In-progress deals — full list" data-id="element-316" />
      <div className="tw" data-id="element-317">
        <table className="tbl" data-id="element-318">
          <thead data-id="element-319">
            <tr data-id="element-320">
              <th style={{
              width: '11%'
            }} data-id="element-321">DR ID</th>
              <th style={{
              width: '22%'
            }} data-id="element-322">Account</th>
              <th style={{
              width: '15%'
            }} data-id="element-323">SC Assigned</th>
              <th style={{
              width: '14%'
            }} data-id="element-324">Offering</th>
              <th style={{
              width: '7%'
            }} data-id="element-325">Age</th>
              <th style={{
              width: '10%'
            }} data-id="element-326">GNARR</th>
              <th style={{
              width: '9%'
            }} data-id="element-327">Close Q</th>
              <th style={{
              width: '7%'
            }} data-id="element-328">Region</th>
              <th style={{
              width: '5%'
            }} data-id="element-329">Sub</th>
            </tr>
          </thead>
          <tbody data-id="element-330">
            {searchRows ?? dealsSorted.map(d => <tr key={d.id} data-id="element-331">
                <td data-id="element-332"><span style={{
                fontSize: 9,
                color: '#8892a0'
              }} data-id="element-333">{d.id}</span></td>
                <td title={d.account} data-id="element-334">{d.account.substring(0, 24)}</td>
                <td title={d.sc} data-id="element-335">{d.sc.split(' ').slice(0, 2).join(' ')}</td>
                <td data-id="element-336">
                  <span className="badge b-blue" style={{
                fontSize: 8
              }} data-id="element-337">
                    {d.offering.replace('Engagement', 'Eng.').replace('Support', 'Sup.')}
                  </span>
                </td>
                <td className={d.age > 90 ? 'c-red' : d.age > 45 ? 'c-gold' : ''} style={{
              fontWeight: d.age > 90 ? 600 : 400
            }} data-id="element-338">{d.age}d</td>
                <td data-id="element-339">{fmtG(d.gnarr)}</td>
                <td data-id="element-340">{d.close_q}</td>
                <td data-id="element-341">{regionBadge(d.region)}</td>
                <td data-id="element-342"><span className="badge b-gray" style={{
                fontSize: 8
              }} data-id="element-343">{d.sub_region}</span></td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}